import { useAuth0 } from "@auth0/auth0-react"
import TextSnippetIcon from "@mui/icons-material/TextSnippet"
import { Alert, Snackbar } from "@mui/material"
import React, { useEffect, useState } from "react"
import {
  Button,
  Col,
  Form,
  Modal,
  OverlayTrigger,
  Row,
  Tooltip,
} from "react-bootstrap"
import { API_ENDPOINTS } from "../../../../api/endpoints"
import { useSpinner } from "../../../../contexts/SpinnerProvider"
import { useApi } from "../../../../hooks/useApi"
import { IEventos } from "../../../../interfaces/IEventos"
import { ILinkPublicoResponse } from "../../../../interfaces/ILinkPublico"
import {
  FormaDePagoLabels,
  IRecibo,
  IReciboEventoRequest,
  RecibosEventoModalProps,
  USUARIOS_CARGA,
} from "../../../../interfaces/IRecibo"
import { TipoEvento } from "../../../../interfaces/TipoEvento"
import { formatDateIsoWithTime } from "../../../../utils/dateUtils"
import { obtenerAdicionalesRecibo } from "../../../../utils/reciboUtils"
import {
  buildWhatsappUrl,
  buildReciboPublicLinkMessage,
  formatPublicLinkExpiration,
} from "../../../../utils/publicLinkUtils"
import { notifyError } from "../../../../api/notify"
import TelefonoWhatsappModal from "../../../TelefonoWhatsappModal/TelefonoWhatsappModal"
import AddReciboEventoModal from "./AddReciboEventoModal/AddReciboEventoModal"
import "../RecibosModal/RecibosModal.css"

const RecibosEventoModal: React.FC<RecibosEventoModalProps> = ({
  show,
  handleClose,
  handleReciboEventoAdd,
  idEvento,
  tipoEvento,
  nombreEvento,
  telefonoEvento,
  nombreClienteEvento,
}) => {
  const mostrarBaile = tipoEvento === TipoEvento.CUMPLEANIOS_BODAS
  const [recibos, setRecibos] = useState<IRecibo[]>([])
  const [showAddModal, setShowAddModal] = useState(false)
  const { getAccessTokenSilently } = useAuth0()
  const api = useApi()
  const { addLoading, removeLoading } = useSpinner()
  const API_URL_RECIBOS = API_ENDPOINTS.RECIBOS
  const API_URL_EVENTOS = API_ENDPOINTS.EVENTOS
  const [usuarioCarga, setUsuarioCarga] = useState<string>(
    () => localStorage.getItem("usuarioCargaRecibo") || USUARIOS_CARGA[0],
  )
  const [reciboAAnular, setReciboAAnular] = useState<IRecibo | null>(null)
  const [showConfirmAnularModal, setShowConfirmAnularModal] = useState(false)
  const [showMotivoAnularModal, setShowMotivoAnularModal] = useState(false)
  const [motivoAnulacionInput, setMotivoAnulacionInput] = useState("")
  const [copyMessage, setCopyMessage] = useState("")
  const [telefonoEventoActual, setTelefonoEventoActual] = useState("")
  const [reciboWhatsappPendiente, setReciboWhatsappPendiente] =
    useState<number | null>(null)
  const [telefonoWhatsapp, setTelefonoWhatsapp] = useState("")
  const [guardandoTelefonoWhatsapp, setGuardandoTelefonoWhatsapp] =
    useState(false)

  const handleUsuarioCargaChange = (usuario: string) => {
    setUsuarioCarga(usuario)
    localStorage.setItem("usuarioCargaRecibo", usuario)
  }

  const callApiAndSetRecibosEvento = async () => {
    if (!idEvento) return
    addLoading()
    try {
      const data = await api.get<IRecibo[]>(
        `${API_URL_RECIBOS}/evento/${idEvento}`,
        { errorMessage: "Error al obtener los recibos del evento" },
      )
      setRecibos(data)
    } catch {
      // toast ya mostrado
    } finally {
      removeLoading()
    }
  }

  useEffect(() => {
    if (show) callApiAndSetRecibosEvento()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, idEvento])

  useEffect(() => {
    setTelefonoEventoActual(telefonoEvento || "")
  }, [telefonoEvento])

  const handleNuevoRecibo = async (recibo: IReciboEventoRequest) => {
    await handleReciboEventoAdd(recibo)
    await callApiAndSetRecibosEvento()
  }

  const iniciarAnulacion = (recibo: IRecibo) => {
    setReciboAAnular(recibo)
    setShowConfirmAnularModal(true)
  }

  const handleConfirmAnular = (confirmado: boolean) => {
    setShowConfirmAnularModal(false)
    if (confirmado) setShowMotivoAnularModal(true)
    else setReciboAAnular(null)
  }

  const handleEnviarMotivoAnulacion = async () => {
    if (!reciboAAnular) return
    addLoading()
    try {
      await api.post(
        `${API_URL_RECIBOS}/${reciboAAnular.id}/anular`,
        {
          motivoAnulacion: motivoAnulacionInput.trim() || "-",
          usuarioAnulacion: usuarioCarga,
        },
        { errorMessage: "Error al anular el recibo" },
      )
      await callApiAndSetRecibosEvento()
      setShowMotivoAnularModal(false)
      setMotivoAnulacionInput("")
      setReciboAAnular(null)
    } catch {
      // toast ya mostrado
    } finally {
      removeLoading()
    }
  }

  const fetchReciboPdf = async (reciboId: number, esImprimir: boolean): Promise<Blob> => {
    const token = await getAccessTokenSilently()
    const query = esImprimir ? "?esImprimir=true" : ""
    const response = await fetch(`${API_URL_RECIBOS}/${reciboId}/pdf${query}`, {
      method: "GET",
      headers: { Authorization: `Bearer ${token}` },
    })
    if (!response.ok) throw new Error("Error al obtener el PDF")
    return response.blob()
  }

  const handleDescargarRecibo = async (reciboId: number, numeroRecibo: string) => {
    addLoading()
    try {
      const blob = await fetchReciboPdf(reciboId, false)
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement("a")
      link.href = url
      link.download = `Evento - recibo Nº ${numeroRecibo}.pdf`
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
    } catch (error) {
      notifyError("Error al descargar el recibo", error)
    } finally {
      removeLoading()
    }
  }

  const handleImprimirRecibo = async (reciboId: number) => {
    addLoading()
    try {
      const blob = await fetchReciboPdf(reciboId, true)
      const url = window.URL.createObjectURL(blob)
      const printWindow = window.open(url)
      if (!printWindow) throw new Error("No se pudo abrir la ventana de impresion")
      printWindow.focus()
      printWindow.onload = () => printWindow.print()
    } catch (error) {
      notifyError("Error al imprimir el recibo", error)
    } finally {
      removeLoading()
    }
  }

  const obtenerTelefonoEvento = (): string => telefonoEventoActual.trim()

  const sendReciboEventoWhatsapp = async (
    reciboId: number,
    telefonoCliente: string,
  ) => {
    addLoading()
    try {
      const link = await api.post<ILinkPublicoResponse>(
        `${API_URL_RECIBOS}/${reciboId}/link-publico-pdf`,
        undefined,
        { errorMessage: "Error al generar el link publico del recibo" },
      )
      const whatsappUrl = buildWhatsappUrl(
        telefonoCliente,
        buildReciboPublicLinkMessage(
          nombreClienteEvento || "",
          link.url,
          link.expiraEn,
        ),
      )
      if (!whatsappUrl) {
        setCopyMessage("No hay telefono valido para enviar por WhatsApp")
        setTimeout(() => setCopyMessage(""), 3000)
        return
      }
      window.open(whatsappUrl, "_blank", "noopener,noreferrer")
      setCopyMessage(
        `WhatsApp abierto, el link expira el ${formatPublicLinkExpiration(
          link.expiraEn,
        )}`,
      )
      setTimeout(() => setCopyMessage(""), 3000)
    } catch (error) {
      notifyError("Error al enviar el recibo por WhatsApp", error)
    } finally {
      removeLoading()
    }
  }

  const handleEnviarReciboWhatsapp = async (reciboId: number) => {
    const telefonoCliente = obtenerTelefonoEvento()
    if (!telefonoCliente) {
      setReciboWhatsappPendiente(reciboId)
      setTelefonoWhatsapp("")
      return
    }
    await sendReciboEventoWhatsapp(reciboId, telefonoCliente)
  }

  const handleGuardarTelefonoYEnviarRecibo = async () => {
    if (!idEvento || !reciboWhatsappPendiente || !telefonoWhatsapp.trim()) {
      return
    }
    setGuardandoTelefonoWhatsapp(true)
    addLoading()
    try {
      const evento = await api.get<IEventos>(`${API_URL_EVENTOS}/${idEvento}`, {
        errorMessage: "Error al obtener el evento",
      })
      await api.put<IEventos>(
        `${API_URL_EVENTOS}/${idEvento}`,
        { ...evento, telefono: telefonoWhatsapp.trim() },
        { errorMessage: "Error al guardar el telefono" },
      )
      const telefonoGuardado = telefonoWhatsapp.trim()
      const reciboId = reciboWhatsappPendiente
      setTelefonoEventoActual(telefonoGuardado)
      setReciboWhatsappPendiente(null)
      setTelefonoWhatsapp("")
      await sendReciboEventoWhatsapp(reciboId, telefonoGuardado)
    } catch {
      // toast ya mostrado
    } finally {
      removeLoading()
      setGuardandoTelefonoWhatsapp(false)
    }
  }

  const getClass = (isAnulado: boolean) =>
    isAnulado ? "recibo-anulado text-center" : "text-center"

  const renderAdicionales = (recibo: IRecibo) => {
    const adicionales = obtenerAdicionalesRecibo(recibo)
    if (adicionales.length === 0) return "-"

    return (
      <div style={{ textAlign: "left" }}>
        {adicionales.map((adicional) => (
          <div key={adicional.label}>
            {adicional.label}: {adicional.cantidad}
          </div>
        ))}
      </div>
    )
  }

  return (
    <>
      <Modal
        show={show}
        onHide={handleClose}
        dialogClassName="modal-doble-ancho-alto"
        className={showAddModal ? "modal-blurred" : ""}
      >
        <Modal.Header closeButton>
          <Modal.Title>
            Recibos del Evento{" "}
            <span className="egresado-datos">
              {nombreEvento || "Cargando..."}
            </span>
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Row className="mb-3 align-items-end">
            <Col xs="auto">
              <Button variant="primary" onClick={() => setShowAddModal(true)}>
                Cargar recibo <i className="bi bi-plus-square"></i>
              </Button>
            </Col>
            <Col xs="auto">
              <OverlayTrigger
                placement="top"
                overlay={
                  <Tooltip id="tooltip-usuario-carga">
                    Usuario de Carga/Anulación
                  </Tooltip>
                }
              >
                <Form.Select
                  value={usuarioCarga}
                  onChange={(e) => handleUsuarioCargaChange(e.target.value)}
                  style={{ width: "122px" }}
                >
                  {USUARIOS_CARGA.map((u) => (
                    <option key={u} value={u}>{u}</option>
                  ))}
                </Form.Select>
              </OverlayTrigger>
            </Col>
          </Row>

          <div className="table-responsive mt-3">
            <table className="table table-bordered table-hover">
              <thead>
                <tr>
                  <th className="text-center">Nº</th>
                  <th className="text-center">Número</th>
                  <th className="text-center">Fecha y hora</th>
                  <th className="text-center">Adulto</th>
                  <th className="text-center">Menor</th>
                  {mostrarBaile && <th className="text-center">Baile</th>}
                  {mostrarBaile && (
                    <th className="text-center" style={{ minWidth: "145px" }}>
                      Adicionales
                    </th>
                  )}
                  <th className="text-center">Seña</th>
                  <th className="text-center">Forma de Pago</th>
                  <th className="text-center">Total</th>
                  <th className="text-center">Estado</th>
                  <th className="text-center">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {recibos.length > 0 ? (
                  recibos.map((recibo, index) => (
                    <tr key={index}>
                      <td className={getClass(recibo.isAnulado)}>{index + 1}</td>
                      <td className={getClass(recibo.isAnulado)}>{recibo.numero}</td>
                      <td className={getClass(recibo.isAnulado)}>
                        {formatDateIsoWithTime(recibo.fecha)}
                      </td>
                      <td className={getClass(recibo.isAnulado)}>{recibo.tarjetasAdulto}</td>
                      <td className={getClass(recibo.isAnulado)}>{recibo.tarjetasMenor}</td>
                      {mostrarBaile && (
                        <td className={getClass(recibo.isAnulado)}>
                          {recibo.tarjetasBaile ?? 0}
                        </td>
                      )}
                      {mostrarBaile && (
                        <td className={getClass(recibo.isAnulado)}>
                          {renderAdicionales(recibo)}
                        </td>
                      )}
                      <td className={getClass(recibo.isAnulado)}>
                        {recibo.tarjetasAdulto > 0 ||
                        recibo.tarjetasMenor > 0 ||
                        (recibo.tarjetasBaile ?? 0) > 0 ||
                        obtenerAdicionalesRecibo(recibo).length > 0 ? (
                          <OverlayTrigger
                            placement="top"
                            overlay={<Tooltip id={`tooltip-tarjetas-${recibo.id}`}>Se pagaron tarjetas</Tooltip>}
                          >
                            <i className="bi bi-credit-card-fill text-primary fs-5"></i>
                          </OverlayTrigger>
                        ) : recibo.seniaDePago === true ? (
                          <OverlayTrigger
                            placement="top"
                            overlay={
                              <Tooltip id={`tooltip-senia-${recibo.id}`}>
                                Seña: ${Number(recibo.totalRecibo).toLocaleString("de-DE", {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                })}
                              </Tooltip>
                            }
                          >
                            <i className="bi bi-check-circle-fill text-success fs-5"></i>
                          </OverlayTrigger>
                        ) : null}
                      </td>
                      <td className={getClass(recibo.isAnulado)}>
                        {recibo.detallesPago && recibo.detallesPago.length > 0 ? (
                          recibo.detallesPago.length === 1 ? (
                            FormaDePagoLabels[recibo.detallesPago[0].formaDePago]
                          ) : (
                            <div style={{ textAlign: "left" }}>
                              {recibo.detallesPago.map((d, idx) => (
                                <div key={idx}>
                                  {FormaDePagoLabels[d.formaDePago]}: $
                                  {d.monto.toLocaleString("de-DE", {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                  })}
                                </div>
                              ))}
                            </div>
                          )
                        ) : "N/A"}
                      </td>
                      <td className={getClass(recibo.isAnulado)}>
                        ${recibo.totalRecibo.toLocaleString("de-DE", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </td>
                      <td className="text-center">
                        {recibo.isAnulado ? (
                          <OverlayTrigger
                            placement="top"
                            overlay={
                              <Tooltip id={`tooltip-${index}`}>
                                Fecha: {formatDateIsoWithTime(recibo.fechaAnulacion)}
                                <br />
                                Motivo: {recibo.motivoAnulacion}
                              </Tooltip>
                            }
                          >
                            <span className="text-danger fw-bold">Anulado</span>
                          </OverlayTrigger>
                        ) : (
                          <span className="text-success fw-bold">Activo</span>
                        )}
                      </td>
                      <td>
                        <div className="d-flex justify-content-center gap-3">
                          <OverlayTrigger
                            placement="top"
                            overlay={<Tooltip id="tooltip-descargar">Descargar recibo</Tooltip>}
                          >
                            <Button
                              variant="success"
                              size="sm"
                              onClick={() => handleDescargarRecibo(recibo.id, recibo.numero)}
                              className="py-0 px-2"
                            >
                              <i className="bi bi-download fs-5"></i>
                            </Button>
                          </OverlayTrigger>
                          <OverlayTrigger
                            placement="top"
                            overlay={
                              <Tooltip id="tooltip-link-publico-recibo">
                                Enviar recibo por WhatsApp
                              </Tooltip>
                            }
                          >
                            <Button
                              variant="success"
                              size="sm"
                              onClick={() => handleEnviarReciboWhatsapp(recibo.id)}
                              className="py-0 px-2"
                            >
                              <span className="recibo-whatsapp-icon" aria-hidden="true">
                                <TextSnippetIcon className="recibo-whatsapp-icon__doc" />
                                <i className="bi bi-whatsapp recibo-whatsapp-icon__wa"></i>
                              </span>
                            </Button>
                          </OverlayTrigger>
                          <OverlayTrigger
                            placement="top"
                            overlay={<Tooltip id="tooltip-imprimir">Imprimir recibo</Tooltip>}
                          >
                            <Button
                              variant="primary"
                              size="sm"
                              onClick={() => handleImprimirRecibo(recibo.id)}
                              className="py-0 px-2"
                            >
                              <i className="bi bi-printer fs-5"></i>
                            </Button>
                          </OverlayTrigger>
                          <OverlayTrigger
                            placement="top"
                            overlay={<Tooltip id="tooltip-anular">Anular recibo</Tooltip>}
                          >
                            <Button
                              variant="danger"
                              size="sm"
                              onClick={() => iniciarAnulacion(recibo)}
                              className="py-0 px-2"
                              disabled={recibo.isAnulado}
                            >
                              <i className="bi bi-x-circle fs-5"></i>
                            </Button>
                          </OverlayTrigger>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={mostrarBaile ? 12 : 10}>
                      <div className="no-result-message">
                        No se encontraron resultados
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </Modal.Body>

        <AddReciboEventoModal
          show={showAddModal}
          handleClose={() => setShowAddModal(false)}
          onReciboCreado={handleNuevoRecibo}
          eventoId={parseInt(idEvento)}
          usuarioCarga={usuarioCarga}
          tipoEvento={tipoEvento}
        />
      </Modal>

      <Snackbar
        open={!!copyMessage}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        autoHideDuration={3000}
      >
        <Alert severity="success">{copyMessage}</Alert>
      </Snackbar>

      <TelefonoWhatsappModal
        show={reciboWhatsappPendiente !== null}
        nombre={nombreClienteEvento || nombreEvento || "el cliente"}
        telefono={telefonoWhatsapp}
        onTelefonoChange={setTelefonoWhatsapp}
        onCancel={() => {
          setReciboWhatsappPendiente(null)
          setTelefonoWhatsapp("")
        }}
        onConfirm={handleGuardarTelefonoYEnviarRecibo}
        isSaving={guardandoTelefonoWhatsapp}
      />

      <Modal
        show={showConfirmAnularModal}
        onHide={() => setShowConfirmAnularModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Confirmar anulación</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          ¿Estás seguro de que deseas anular el recibo número{" "}
          <strong>{reciboAAnular?.numero}</strong>?
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => handleConfirmAnular(false)}>
            No
          </Button>
          <Button variant="danger" onClick={() => handleConfirmAnular(true)}>
            Sí
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showMotivoAnularModal}
        onHide={() => setShowMotivoAnularModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Motivo de anulación</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group controlId="motivoAnulacionTextarea">
              <Form.Label>Por favor, ingresa el motivo de la anulación:</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={motivoAnulacionInput}
                onChange={(e) => setMotivoAnulacionInput(e.target.value)}
                placeholder="Motivo de la anulación"
                autoFocus
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setShowMotivoAnularModal(false)
              setMotivoAnulacionInput("")
              setReciboAAnular(null)
            }}
          >
            Cancelar
          </Button>
          <Button variant="danger" onClick={handleEnviarMotivoAnulacion}>
            Confirmar anulación
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  )
}

export default RecibosEventoModal
